import {isBoolean, pickBy} from 'lodash';
import {createSelector} from 'reselect';

import {isDevelopment} from '../../../libs/shared/helpers/src/environment/envHelpers';
import {isMobile} from '../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {AppParametricSelector} from '../../helpers/reselectHelpers';
import {trackFlagRequested} from '../../services/featureFlags';
import {AppState} from '../appState';
import {FeatureFlagsMap} from './featureFlagsState';
import {FeatureFlagsEnum} from './featureFlagsTypes';

/*
 * Constants.
 */

/*
 * Common selectors.
 */

function getDynamicFeatureFlags(state: AppState) {
  return state.featureFlags.dynamic;
}
export function getInitialFeatureFlags(state: AppState) {
  return state.featureFlags.initial;
}

function baseGetFeatureFlagValue(
  featureFlags: FeatureFlagsMap,
  name: FeatureFlagsEnum,
  defaultValue: boolean,
) {
  const rawValue = featureFlags[name];
  const value = isBoolean(rawValue) ? rawValue : defaultValue;

  try {
    trackFlagRequested(name, value);
  } catch {
    // Ignore.
  }

  return value;
}

/**
 * Create a selector that will returns a map with only selected featureFlagNames.
 */
export function makeGetInitialFeatureFlagsSubset(featureFlagNames: ReadonlyArray<string>) {
  return createSelector(
    getInitialFeatureFlags,
    (featureFlags): FeatureFlagsMap =>
      pickBy(featureFlags, (value, featureFlagKey) => featureFlagNames.includes(featureFlagKey)),
  );
}

/** Creates a selector that will query the value of a feature flag with real time updates. */
function makeGetHasDynamicFeatureFlag(name: FeatureFlagsEnum, defaultValue = false) {
  return createSelector(getDynamicFeatureFlags, (featureFlags) =>
    baseGetFeatureFlagValue(featureFlags, name, defaultValue),
  );
}

/** Creators a selector that will query the initial value of a feature flag. */
export function makeGetHasInitialFeatureFlag(name: FeatureFlagsEnum, defaultValue = false) {
  return createSelector(getInitialFeatureFlags, (featureFlags) =>
    baseGetFeatureFlagValue(featureFlags, name, defaultValue),
  );
}

/*
 * Feature flag selectors.
 */

export type GetFeatureFlagSelector = AppParametricSelector<any, boolean>;

// Store the value in Redux for live updates.
export const getHasOfflineCache = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.OFFLINE_CONVERSATION_CACHE,
  isDevelopment(),
);
export const getHasNewConversationContentPrefetch = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.NEW_P3_PREFETCH,
  false,
);
export const getHasNewPrefetchV3 = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.NEW_PREFETCH_V3, false);
export const getHasMoreP2Prefetch = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.MORE_P2_PREFETCH, false);
export const getHasOnlineClientCounters = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.ONLINE_CLIENT_COUNTERS,
  false,
);
export const getHasFetchUpdatedConversationThrottling = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.CONVERSATION_UPDATED_THIN_THROTTLING,
  isDevelopment(),
);
export const getHasServiceWorkerBootCachePatching = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SW_BOOT_CACHE_PATCHING,
  false,
);
export const getHasRecentCommunicationsBanner = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.RECENT_COMMUNICATIONS_BANNER,
  false,
);
export const getHasRecentCommunicationsComposerBanner = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.RECENT_COMMUNICATIONS_COMPOSER_BANNER,
  false,
);
export const getHasNewInboxSettingsDetails = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.NEW_INBOX_SETTINGS_DETAILS,
  false,
);

export const getHasCannedAnswersResourceCustomFields = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CANNED_ANSWERS_RESOURCE_CUSTOM_FIELDS,
  isDevelopment(),
);

export const getHasCompanyCards = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.COMPANY_CARDS, false);
export const getHasOldDesktopVersionBanner = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.OLD_DESKTOP_VERSION_BANNER,
  isDevelopment(),
);
// Use initial boot value.
export const getHasPushRenderThrottling = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.PUSH_RENDER_THROTTLING,
  isDevelopment(),
);
export const getHasBootConversationContentPrefetch = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.BOOT_P3_PREFETCH,
  false,
);
export const getHasGuidedDemoFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.GUIDED_TRIAL_EXPERIENCE,
  false,
);
export const getHasStartGuideFlag = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.HELP_GUIDE, false);
export const getHasNotificationCenter = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NOTIFICATION_CENTER,
  false,
);
export const getShouldHideAuditTrail = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.HIDE_AUDIT_TRAIL, false);
export const getIsChatWidgetUnavailable = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SUPPORT_CHAT_FALLBACK,
  false,
);
export const getHasCellsEmptySetupActions = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.P2_CTAS, false);
export const getHasActionCtasBundle = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.ADD_TEAMMATE_AND_INBOX_CTAS,
  false,
);
export const getHasServiceWorker = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.SERVICE_WORKER, false);
export const getHasNonGlobalAdminStartGuideFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.START_GUIDE_TEAMMATES,
  false,
);
export const getHasTableEdition = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.COMPOSER_TABLE_SERVICE,
  false,
);
export const getHasParagraphSpaceIndent = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.COMPOSER_PARAGRAPH_SPACE_INDENTATION,
  false,
);
export const getHasUpsellCards = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.UPSELL_CARDS, isDevelopment());

export const getHasGifs = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.HACKATHON_GIFS, false);

export const getHasEmailGifs = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.GIPHY_EMAIL_INTEGRATION, false);

export const getHasNewTeammateInvite = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_TEAMMATE_INVITE,
  false,
);

export const getHasRuleFixUnrepliedAfterCondition = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.RULE_FIX_UNREPLIED_AFTER_CONDITION,
  isDevelopment(),
);

export const getHasResolveDrafts = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.RESOLVE_DRAFTS, false);
export const getHasLegacyMessageTemplatesSorting = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.LEGACY_CANNED_RESPONSE_SORTING,
  false,
);

export const getHasLenses = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.LENSES, false);

export const getCanUseSearchRawEndpoint = createSelector(
  makeGetHasInitialFeatureFlag(FeatureFlagsEnum.SEARCH_USE_SEARCH_RAW_ENDPOINT, false),
  (hasFeatureFlag) => hasFeatureFlag && !isMobile(),
);

export const getShouldRulesWrapTriggerConditions = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.RULES_SHOULD_WRAP_TRIGGER_CONDITIONS,
  false,
);

export const getHasDatadogRum = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.DATADOG_RUM, false);
export const getHasNoPendo = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.NO_PENDO, false);

export const getHasChatPresenceIndicatorsOnOffLine = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CHAT_PRESENCE_INDICATORS_ON_OFF_LINE,
  false,
);

// Feature flag for the Accounts Beta
export const getHasNewContactsManagerAccounts = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_CONTACTS_MANAGER_ACCOUNTS,
  isDevelopment(),
);

export const getHasNewContactsManagerV0 = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_CONTACTS_MANAGER_V0,
  isDevelopment(),
);

export const getHasNewVisitorUrlExperience = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_VISITOR_URL_EXPERIENCE,
  false,
);

export const getHasNewChatSeenIndicator = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_CHAT_SEEN_INDICATOR,
  false,
);

export const getHasPushReconnectOnBacklog = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.PUSH_RECONNECT_ON_BACKLOG,
  isDevelopment(),
);

export const getHasNewMoveToUnreachable = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.MOVE_TO_UNREACHABLE,
  isDevelopment(),
);

export const getHasDragAndDropLogging = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.LOG_DRAG_AND_DROP_OPERATIONS,
  isDevelopment(),
);

export const getHasGcRemoveCycles = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.GC_REMOVE_CYCLES, false);

export const getHasLazyTeammates = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.LAZY_TEAMMATES, false);
export const getHasLazyTeammatesDropdowns = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.LAZY_TEAMMATES_DROPDOWNS,
  false,
);

export const getHasCommentTombstone = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.COMMENT_TOMBSTONE, false);

export const getHasContactsManagerExportContacts = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.CONTACTS_MANAGER_EXPORT_CONTACTS,
  false,
);

export const getHasVisitorExperience = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.VISITOR_EXPERIENCE,
  false,
);

export const getHasVisitorExperienceV3 = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.VISITOR_EXPERIENCE_V3,
  false,
);

export const getHasFindMyDrafts2 = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.FIND_MY_DRAFTS_2_LIST_COMMENT_DRAFTS_IN_DRAFTS_FOLDER,
  false,
);

export const getHasInstantReschedulingLink = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.INSTANT_RESCHEDULE,
  false,
);

export const getHasNewPusherOnReconnect = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_PUSHER_ON_RECONNECT,
  false,
);
export const getHasPusherAblyComparison = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.PUSHER_ABLY_COMPARISON,
);

export const getHasAblyAuthority = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.ABLY_FE_AUTHORITY);

export const getHasRuleEditorHighlightErrors = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.RULE_EDITOR_HIGHLIGHT_ERRORS,
  false,
);

export const getHasP1Alphabetization = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.P1_ALPHABETIZATION,
  false,
);

export const getHasAccountsSearch = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.ACCOUNTS_SEARCH, false);

export const getHasAccountsSetupGuide = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.ACCOUNTS_SETUP_GUIDE,
  isDevelopment(),
);

export const getHasPushReduxBatching = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.PUSH_REDUX_BATCHING,
  false,
);

export const getHasDefaultVideoConferencing = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.DEFAULT_VIDEO_CONFERENCING,
  false,
);

export const getHasP4MultiView = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.P4_MULTI_VIEW, false);

export const getHasAutolinkers = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.SETTINGS_AUTOLINKERS, false);

export const getHasListSendingMessagesInSentFolder = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.LIST_SENDING_MESSAGES_IN_SENT_FOLDER,
  false,
);

export const getHasP1AutoRec = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.P1_AUTO_REC, false);

export const getHasP2QuickActions = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.P2_QUICK_ACTIONS, false);

export const getHasGrammarlyIntegration = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.GRAMMARY_INTEGRATION,
  false,
);

export const getHasGrammarlyAutoComplete = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.GRAMMARLY_AUTOCOMPLETE,
  false,
);

export const getHasQuoteComment = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.QUOTE_COMMENT, false);

export const getHasMobileStorageLogging = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.MOBILE_STORAGE_LOGGING,
  isDevelopment(),
);

export const getHasMobileEditContact = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.MOBILE_EDIT_CONTACT,
  false,
);

export const getHasMobileEditContactDescriptionAndLinks = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.MOBILE_EDIT_CONTACT_DESCRIPTION_LINKS,
  false,
);

export const getHasRemoveAddedCalendars = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.HAS_REMOVED_ADDED_CALENDARS,
  false,
);

export const getHasInboxZeroAnimations = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.INBOX_ZERO_ANIMATIONS,
  false,
);

export const getHasRulesDryRun = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.RULES_DRY_RUN,
  isDevelopment(),
);

export const getHasSearchAccountFilter = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SEARCH_ACCOUNT_FILTER,
  isDevelopment(),
);

export const getHasPerformanceDebugLogging = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.PERFORMANCE_DEBUG_LOGGING,
  false,
);

export const getHasPerformanceMetricLoggingDesktop = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.PERFORMANCE_METRIC_LOGGING_DESKTOP,
  false,
);

export const getHasPerformanceMetricLoggingMobile = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.PERFORMANCE_METRIC_LOGGING_MOBILE,
  false,
);

export const getHasPerformanceMetricLogging = createSelector(
  [getHasPerformanceMetricLoggingDesktop, getHasPerformanceMetricLoggingMobile],
  (hasPerformanceMetricLoggingDesktop, hasPerformanceMetricLoggingMobile) =>
    isMobile() ? hasPerformanceMetricLoggingMobile : hasPerformanceMetricLoggingDesktop,
);

export const getHasP3LinkTracking = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.P3_LINK_TRACKING,
  isDevelopment(),
);

export const getHasAutoShareDrafts = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.AUTO_SHARE_DRAFTS, false);

export const getHasLocalApplications = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.LOCAL_APPLICATIONS,
  false,
);

export const getHasFrontChatAdvancedEditor = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.FRONT_CHAT_ADVANCED_EDITOR,
  isDevelopment(),
);

export const getHasAccountMappedCsvImport = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.ACCOUNT_MAPPED_CSV_IMPORT,
  isDevelopment(),
);

export const getHasCircles = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.CIRCLES, false);

export const getHasTableBasedRules = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.TABLE_BASED_RULES,
  isDevelopment(),
);

export const getHasGuestInviteNewCopy = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.GUEST_INVITE_NEW_COPY,
  false,
);

export const getHasNewIntegrationsDiscoverability = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_INTEGRATIONS_DISCOVERABILITY,
  false,
);

export const getHasNewIntegrationsDiscoverabilityP4 = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_INTEGRATIONS_DISCOVERABILITY_P4,
  false,
);

export const getComposerReplacePilledVariablesInMessageTemplates = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.COMPOSER_REPLACE_PILLED_VARIABLES_IN_MESSAGE_TEMPLATES,
  false,
);

export const getHasShow15MinuteEvents = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SHOW_15_MINUTE_EVENTS,
  false,
);

export const getHasNewRecurrenceCilent = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.NEW_RECURRENCE_CLIENT,
  false,
);

export const getHasNotifyCalendarGuests = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.NOTIFY_CALENDAR_GUESTS,
  false,
);

export const getHasCalendarAttachments = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.CALENDAR_ATTACHMENTS,
  false,
);

export const getHasRtlLanguageSupport = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.RTL_LANGUAGE_SUPPORT,
  false,
);

export const getHasInboxesInFrontClient = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.INBOXES_FRONT_CLIENT,
  false,
);

export const getHasBusinessHoursInbox = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.BUSINESS_HOURS_INBOX,
  true,
);

export const getHasCrmSyncFullPageSalesforce = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.CRM_SYNC_FULL_PAGE_SALESFORCE,
  false,
);

export const getHasMicrosoftDynamicsAccountSync = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.MICROSOFT_DYNAMICS_ACCOUNT_SYNC,
  false,
);

export const getHasNewApplicationsEntryPoints = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.NEW_APPLICATIONS_ENTRY_POINTS,
  false,
);

export const getHasP2P3DarkMode = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.P2_P3_DARK_MODE, false);

export const getHasNativeDeleteInEmailComposer = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.NATIVE_DELETE_EMAIL_COMPOSER,
  false,
);

export const getHasBulletedListsV2 = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.BULLETED_LISTS_V2, false);

export const getHasHighlightAlgorithms = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SEARCH_HIGHLIGHT_ALGORITHMS,
  false,
);

export const getHasDeveloperSettings = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.DEVELOPER_SETTINGS,
  false,
);

export const getHasSendgridSubuser = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SENDGRID_DNS_SUBUSER,
  false,
);
